// Kent State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Kent State's Common Data Set 2025-2026 for the Kent Campus (the file
//    "KC CDS_2025-2026-Final_web_0.docx", a Word file on the university's
//    file host www-s3-live.kent.edu; the SOURCES url is the Institutional
//    Research page on kent.edu that links it). C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21/C22 (no early decision, no early action). Its G1/G5
//    cost tables are blank: the CDS checks "2026-2027 costs not available"
//    and gives 7/1 as the date they would be, so the costs come from the
//    financial aid office instead.
//  - Kent State Admissions, First-Year Student Admissions (Kent Campus):
//    important dates, test-optional section, and major and program-specific
//    requirements; and the Test-Optional Policy page. Neither page names an
//    entering year; they are the pages as posted for the application that
//    opened August 1, 2026. Quirk: the admissions FAQ page says the fall
//    deadline is June 1, while the first-year page says the application and
//    all materials are due seven days before the semester starts. The page
//    uses the first-year page and does not cite the FAQ.
//  - Kent State Financial Aid, Cost of Attendance: the 2026-2027 Kent Campus
//    undergraduate table for the fall 2026 cohort (tuition guarantee rates),
//    in-state and out-of-state, living on campus. Its items sum exactly to
//    its totals ($34,420 and $44,870), loan fees included.
//  - College Scorecard, school id 203517 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Kent State Common Data Set 2025-2026, Kent Campus',
        url: 'https://www.kent.edu/ir/common-dataset-cds',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Kent State Admissions, First-Year Student Admissions',
        url: 'https://www.kent.edu/admissions/first-year-student-requirements',
        read: '2026-10-01',
    },
    tests: {
        label: 'Kent State Admissions, Test-Optional Policy',
        url: 'https://www.kent.edu/admissions/test-optional-policy',
        read: '2026-10-01',
    },
    costs: {
        label: 'Kent State Financial Aid, Cost of Attendance',
        url: 'https://www.kent.edu/fbe-center/tuition-and-other-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?203517-Kent-State-University-at-Kent',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'kent-state-university',
    kbId: 'kent_state_university',
    name: 'Kent State University',
    shortName: 'Kent State',
    location: 'Kent, OH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Kent State brand swatches); decoration only.
    branding: {
        primary: '#003976',
        secondary: '#EFAB00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/kent_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 25526,
        admitted: 16015,
        enrolled: 4026,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 16510, admitted: 10028 },
            { group: 'Out-of-state', applied: 5903, admitted: 3421 },
            { group: 'International', applied: 3113, admitted: 2566 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Kent State\'s Common Data Set reports one first-year acceptance rate for the Kent Campus, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Kent State has no early decision or early action plan. Its November 1 priority deadline is an earlier filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'After you are admitted to the Kent Campus, some majors ask for more:',
        rows: [
            { school: 'Aerospace, cybersecurity and mechatronics engineering', requirement: 'At least a 3.0 high school GPA and math placement into Calculus I or beyond. If you fall short, you may be admitted to the related engineering technology major and ask to change after your first year.' },
            { school: 'Architecture, fashion design and fashion merchandising', requirement: 'At least a 3.0 high school GPA. If you fall short, Kent State reviews you holistically and may place you in a pre-major.' },
            { school: 'Interior design and teacher education', requirement: 'At least a 2.75 high school GPA.' },
            { school: 'Speech pathology and audiology', requirement: 'At least a 3.0 high school GPA.' },
            { school: 'Nursing', requirement: 'High school math, biology and chemistry, plus either a 3.0 GPA, or a 2.7 GPA with an ACT composite of 22 or an SAT of 1100. Students not accepted into nursing may be accepted into public health.' },
            { school: 'Music and musical theatre', requirement: 'An audition before admission to the School of Music or the B.F.A. in musical theatre.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'November 1' },
        rows: [
            { plan: 'Priority deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Merit scholarship deadline', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'The application opens August 1. After the priority deadline, your application and all materials must be in seven days before the semester starts. Kent State says admission decisions are released in October and does not publish a decision date for each deadline. National College Decision Day is May 1.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Kent State is test-optional: you do not have to send SAT or ACT scores. On the application you say whether you want scores considered, and that choice cannot be changed online. If you send scores, Kent State uses the highest ACT composite or SAT from a single test date; it does not superscore. You are eligible for merit awards with or without a score.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13850, 24300] },
            { item: 'Student health fee', amounts: [180, 180] },
            { item: 'Career services fee', amounts: [188, 188] },
            { item: 'Food and housing (on campus)', amounts: [14380, 14380] },
            { item: 'Books, course materials and supplies', amounts: [800, 800] },
            { item: 'Transportation', amounts: [2620, 2620] },
            { item: 'Personal expenses', amounts: [2334, 2334] },
            { item: 'Loan fees', amounts: [68, 68] },
        ],
        totals: [34420, 44870],
        note: 'For a full-time undergraduate on the Kent Campus living on campus, fall and spring, at the rates set for students who entered in fall 2026. Kent State sets new cohort rates each year, so fall 2027 rates may differ.',
        source: 'costs',
        netPrice: {
            average: 20294,
            byIncome: [
                { income: '$0 to $30,000', amount: 15853 },
                { income: '$30,001 to $48,000', amount: 15786 },
                { income: '$48,001 to $75,000', amount: 16382 },
                { income: '$75,001 to $110,000', amount: 22661 },
                { income: 'Over $110,000', amount: 26449 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Kent State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
